<template>
  <div :class="['card', variantClass, roundedClass, shadowClass, borderColorClass, $attrs.class]">
    <div
      :class="['card-header', headerClass]"
      v-if="$slots.header"
    >
      <slot name="header" />
    </div>
    <div :class="['card-body', bodyClass]">
      <slot />
    </div>
    <div
      :class="['card-footer', footerClass]"
      v-if="$slots.footer"
    >
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { semanticColors } from '@/constants/colors';

defineOptions({
  name: 'AppCard',
});

const props = defineProps({
  variant: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'muted'].includes(value),
  },
  rounded: {
    type: String,
    default: 'xl',
    validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value),
  },
  shadow: {
    type: String,
    default: 'xs',
    validator: (value) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value),
  },
  borderColor: {
    type: String,
    default: 'neutral',
    validator: (value) => semanticColors.includes(value),
  },
  headerClass: {
    type: String,
    default: '',
  },
  bodyClass: {
    type: String,
    default: '',
  },
  footerClass: {
    type: String,
    default: '',
  },
});

const variantClass = computed(() => {
  return {
    default: 'card-default',
    muted: 'card-muted',
  }[props.variant];
});

const roundedClass = computed(() => {
  return {
    xs: 'rounded-xs',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
  }[props.rounded];
});

const shadowClass = computed(() => {
  return {
    xs: 'shadow-xs',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl',
  }[props.shadow];
});

const borderColorClass = computed(() => {
  return {
    neutral: 'border-gray-200',
    success: 'border-green-300',
    warning: 'border-yellow-300',
    error: 'border-red-300',
    primary: 'border-brand-300',
  }[props.borderColor];
});
</script>

<style>
@reference '@/assets/main.css';

.card {
  @apply flex flex-col bg-white border;

  &.card-muted {
    @apply bg-gray-50 border-none shadow-none;
  }

  & .card-body {
    @apply p-4 flex-1;
  }

  & .card-header {
    @apply p-4 border-b border-gray-200;
  }

  & .card-footer {
    @apply flex items-center p-4 border-t border-gray-200;
  }
}
</style>
